/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import {
  largeLandscape,
  smallLandscape,
} from "assets/jss/material-kit-pro-react.jsx";

export const frostGlass = {
  position: "relative",
  background: "inherit",
  overflow: "hidden",
  "&:before": {
    width: "calc(100%+50px)",
    height: "calc(100%+50px)",
    content: "''",
    position: "absolute",
    top: "-25px",
    left: "-25px",
    bottom: "-25px",
    right: "-25px",
    background: "inherit",
    filter: "blur(6px)",
    boxShadow: "inset 0 0 0 400px rgba(192,192,192,0.25)",
  },
};

export const absoluteFill = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
};

export const coverGlass = {
  margin: 0,
  background: "inherit",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  "&:before": {
    ...frostGlass["&:before"],
    filter: "blur(12px)",
    boxShadow: "inset 0 0 0 800px rgba(37,40,80,0.55)",
  },
  ...absoluteFill,
};

export const openButton = {
  color: "#FFFFFF",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  fontWeight: 400,
  fontFamily: "Open Sans",
  boxShadow:
    "0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 8px 4px -2px rgba(0, 0, 0, 0.2), 0 1px 5px 0 rgba(0, 0, 0, 0.12)",
  borderRadius: "16px",
  padding: "1rem 2rem",
};

export const coverButton = {
  ...openButton,
  margin: "3rem auto",
  "@media (orientation: portrait)": {
    minHeight: "10vh",
    fontSize: "4vh",
  },
  minHeight: "5vw",
  fontSize: "2vw",
  lineHeight: "150%",
  zIndex: 1,
};

export const playerH = "100vw * 0.5625"; // player height
export const headerH = "50px";
export const headboardH = "20vh";
export const tabsHeaderH = "4.5rem";
export const ctrlBarWidth = "3.25rem";
export const tabItemH_L = {
  minHeight: `calc(100vh - ${headerH} - ${headboardH} - ${tabsHeaderH} - 1rem)`,
  maxHeight: `calc(100vh - ${headerH} - ${headboardH} - ${tabsHeaderH} - 1rem)`,
};
export const tabItemH_P = {
  minHeight: `calc(100vh - ${headerH} - ${playerH} - ${ctrlBarWidth} - ${tabsHeaderH} - 0.5rem)`,
  maxHeight: `calc(100vh - ${headerH} - ${playerH} - ${ctrlBarWidth} - ${tabsHeaderH} - 0.5rem)`,
};

export const niceScrollbar = {
  "*::-webkit-scrollbar": {
    width: "0.65em",
    height: "0.65em",
  },
  "*::-webkit-scrollbar-track": {
    "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)",
  },
  "*::-webkit-scrollbar-thumb": {
    backgroundColor: "#8AB058", // green thumb
    // outline: "1px solid slategrey",
  },
};

const styles = (theme) => {
  const primaryColor = theme.palette.primary.main;
  const primaryColorLight = theme.palette.primary.light;
  const secondaryColor = theme.palette.secondary.main;
  const secondaryColorLight = theme.palette.secondary.light;

  const infoColor = theme.palette.info.main;
  const warningColor = theme.palette.warning.main;

  return {
    "@global": niceScrollbar,

    classroom: {
      "@media (min-width: 1024px)": {
        height: "100%", // for large classroom, no scroll
      },
      width: "100%",
    },

    rootGrid: {
      display: "grid",
      position: "relative",
      background: "inherit",

      // Portrait as base case: stack up header, Main, and Side Panel
      gridTemplateColumns: "100%",
      gridTemplateRows: `${headerH} calc(${playerH}) 1fr`,
      gridTemplateAreas: "'.' 'mainX' 'sideX'",
      gridColumnGap: 0,

      // Small Landscape (mobile...): show Main Column only
      [smallLandscape]: {
        gridTemplateColumns: "100%",
        gridTemplateRows: "100%",
        gridTemplateAreas: "'mainX'",
      },

      // Large Landscape: Show full classroom
      [largeLandscape]: {
        "@media (min-width: 1280px)": {
          gridTemplateColumns: "70vw 30vw",
          // gridTemplateColumns: "30vw 70vw", (if put video at right)
        },
        gridTemplateColumns: "73vw 27vw",
        // gridTemplateColumns: "27vw 73vw", (if put video at right)
        gridTemplateRows: "100%",
        gridTemplateAreas: "'mainX sideX'",
        // gridTemplateAreas: "'sideX mainX'", (if put video at right)
      },
    },

    mainColumn: {
      gridArea: "mainX",
      position: "relative",
      background: "inherit",
    },

    mainGrid: {
      display: "grid",
      background: "inherit",

      // Portraid: as base. Show Player only in Main
      gridTemplateColumns: "100%",
      gridTemplateRows: `calc(${playerH})`,
      gridTemplateAreas: "'player'",

      [smallLandscape]: {
        gridTemplateRows: "100%",
      },

      [largeLandscape]: {
        // to allow the first row (PlayerZone) shorter than 1/2 of total height, use "auto 1fr", instead of "repeat(2, 1fr)"
        gridTemplateRows: "auto 1fr",
        gridTemplateAreas: "'player' 'notes'",
        ...absoluteFill,
        height: "100vh",
      },
    },

    // playerZone and notesZone are used by Classroom.tsx, should stay here
    playerZone: {
      gridArea: "player",
      position: "relative",
      background: "inherit",
    },

    notesZone: {
      gridArea: "notes",
      background: "inherit",
      [largeLandscape]: {
        display: "block",
      },
      display: "none", // hide for non-[largeLandscape]
      height: "100%",
      position: "relative",
      // Resume code below if we resize zones via dragging border
      // "&:after": {
      //   // edge bar for resizing height
      //   content: "''",
      //   backgroundColor: "#ccc",
      //   position: "absolute",
      //   top: 0,
      //   height: 4,
      //   width: "100%",
      //   cursor: "ns-resize",
      // },
    },

    // Side Panel related, the parts used by Classroom.tsx
    //
    sideColumn: {
      gridArea: "sideX",
      position: "relative",
      background: "inherit",
      width: "100%",
      [largeLandscape]: {
        height: "100vh",
      },
      [smallLandscape]: {
        display: "none",
      },
      height: `calc(100vh - ${headerH} - ${playerH})`,
      display: "block",
    },

    sideGrid: {
      display: "grid",
      background: "inherit",
      [largeLandscape]: {
        gridTemplateRows: `${headerH} ${headboardH} auto`, // Header, Headboard, Tabs area
        gridTemplateAreas: "'.' 'headBoard' 'tabs'",
      },
      gridTemplateColumns: "100%",
      gridTemplateRows: `${ctrlBarWidth} 1fr`, // Headboard (control only), Tabs
      gridTemplateAreas: "'headBoard' 'tabs'",
    },
    // gridArea 'headBoard' and 'tabs' are defined in sidePanelStyle.jsx
  };
};

export default styles;
